import * as CANNON from 'cannon-es';
import { PhysicsWorld } from './PhysicsWorld';
import { EventBus } from '../core/EventBus';

export interface VehicleConfig {
  mass: number;
  maxSpeed: number;
  acceleration: number;
  brakeForce: number;
  turnSpeed: number;
  friction: number;
}

const DEFAULT_CONFIG: VehicleConfig = {
  mass: 300,
  maxSpeed: 50,        // m/s (180 km/h)
  acceleration: 30,    // m/s²
  brakeForce: 50,      // m/s²
  turnSpeed: 2.5,      // rad/s
  friction: 0.98,      // velocity retention per frame
};

// Arcade physics tuning constants
const PHYSICS_TUNING = {
  BASE_GRAVITY: -12,          // Base downward acceleration
  AIR_GRAVITY_SCALE: 0.6,     // Gravity multiplier when airborne (lower = floatier)
  SPRING_CONSTANT: 80,        // Ground adherence (higher = stiffer)
  DAMPING: 8,                 // Bounce reduction (higher = less bouncy)
  LAUNCH_BOOST_FACTOR: 0.35,  // Vertical velocity from horizontal speed at ramp exit
  BIKE_HEIGHT_OFFSET: 0.5,    // Height above ground for bike center
  AIRBORNE_THRESHOLD: 0.8,    // Height above ground to be considered airborne
};

export class VehiclePhysics {
  public readonly chassis: CANNON.Body;
  private config: VehicleConfig;

  // Current input state
  private engineInput = 0;   // -1 to 1
  private steerInput = 0;    // -1 to 1
  private isBraking = false;

  // Current velocity (local space)
  private forwardSpeed = 0;

  // Speed buff multiplier (from pickups)
  private speedMultiplier = 1.0;

  // Airtime tracking
  private isAirborne = false;
  private airTime = 0;
  private lastGroundHeight = 0;
  private wasOnRamp = false;

  constructor(
    physicsWorld: PhysicsWorld,
    position: CANNON.Vec3,
    config: Partial<VehicleConfig> = {}
  ) {
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Create simple box chassis
    const chassisShape = new CANNON.Box(new CANNON.Vec3(0.5, 0.4, 1.0));

    this.chassis = new CANNON.Body({
      mass: this.config.mass,
      shape: chassisShape,
      position: position,
      angularDamping: 0.9,
      linearDamping: 0.01, // Very low - we handle friction manually
      fixedRotation: false,
    });

    // Prevent the body from rotating on X and Z axes (only allow Y rotation)
    this.chassis.angularFactor = new CANNON.Vec3(0, 1, 0);

    // Collision groups:
    // 1 = environment/ground (we don't collide with this - handle manually)
    // 2 = player
    // 4 = projectiles/enemies
    this.chassis.collisionFilterGroup = 2; // Player group
    this.chassis.collisionFilterMask = 4 | 16; // Collide with projectiles and buildings, not ground

    physicsWorld.addBody(this.chassis);

    // Listen for speed buff events
    EventBus.on('buff:activated', (data) => {
      if (data.type === 'speed') {
        this.speedMultiplier = 1.5; // 50% speed boost
      }
    });

    EventBus.on('buff:expired', (data) => {
      if (data.type === 'speed') {
        this.speedMultiplier = 1.0;
      }
    });
  }

  public setEngineForce(force: number): void {
    this.engineInput = Math.max(-1, Math.min(1, force));
  }

  public setSteerValue(value: number): void {
    this.steerInput = Math.max(-1, Math.min(1, value));
  }

  public setBrake(brake: boolean): void {
    this.isBraking = brake;
  }

  public update(dt: number, groundHeight: number = 0, onRamp: boolean = false): void {
    // Calculate forward direction in world space
    const forward = new CANNON.Vec3(0, 0, 1);
    this.chassis.quaternion.vmult(forward, forward);

    // Get current forward speed
    this.forwardSpeed = this.chassis.velocity.dot(forward);

    // Apply acceleration/deceleration
    if (this.isBraking) {
      // Braking - reduce speed quickly
      if (Math.abs(this.forwardSpeed) > 0.5) {
        const brakeDir = this.forwardSpeed > 0 ? -1 : 1;
        const brakeAccel = forward.scale(brakeDir * this.config.brakeForce * dt);
        this.chassis.velocity.vadd(brakeAccel, this.chassis.velocity);
      } else {
        // Nearly stopped
        this.chassis.velocity.scale(0.9, this.chassis.velocity);
      }
    } else if (this.engineInput !== 0) {
      // Accelerate/reverse (apply speed buff multiplier)
      const effectiveMaxSpeed = this.config.maxSpeed * this.speedMultiplier;
      const targetSpeed = this.engineInput * effectiveMaxSpeed;
      const speedDiff = targetSpeed - this.forwardSpeed;

      // Apply acceleration towards target speed
      const accel = Math.sign(speedDiff) * Math.min(
        Math.abs(speedDiff),
        this.config.acceleration * dt
      );

      const accelVec = forward.scale(accel);
      this.chassis.velocity.vadd(accelVec, this.chassis.velocity);
    } else {
      // No input - apply friction to slow down
      this.chassis.velocity.scale(this.config.friction, this.chassis.velocity);
    }

    // Clamp to max speed (with buff multiplier)
    const effectiveMaxSpeed = this.config.maxSpeed * this.speedMultiplier;
    const currentSpeed = this.chassis.velocity.length();
    if (currentSpeed > effectiveMaxSpeed) {
      this.chassis.velocity.scale(effectiveMaxSpeed / currentSpeed, this.chassis.velocity);
    }

    // Apply steering (only when moving)
    const speedFactor = Math.min(Math.abs(this.forwardSpeed) / 10, 1);
    if (this.steerInput !== 0 && speedFactor > 0.1) {
      // Turn direction depends on whether going forward or backward
      const turnDir = this.forwardSpeed >= 0 ? 1 : -1;
      const turnAmount = this.steerInput * this.config.turnSpeed * speedFactor * turnDir * dt;

      // Apply rotation around Y axis
      const turnQuat = new CANNON.Quaternion();
      turnQuat.setFromAxisAngle(new CANNON.Vec3(0, 1, 0), turnAmount);
      this.chassis.quaternion = turnQuat.mult(this.chassis.quaternion);

      // Also rotate velocity to follow the turn
      turnQuat.vmult(this.chassis.velocity, this.chassis.velocity);
    }

    // Keep bike upright (reset pitch and roll)
    this.stabilize();

    // Handle ground/ramp physics with arcade tuning
    this.handleGroundPhysics(dt, groundHeight, onRamp);
  }

  private handleGroundPhysics(dt: number, groundHeight: number, onRamp: boolean): void {
    const targetY = groundHeight + PHYSICS_TUNING.BIKE_HEIGHT_OFFSET;
    const currentY = this.chassis.position.y;
    const heightAboveGround = currentY - targetY;

    // Detect airborne state
    const wasAirborne = this.isAirborne;
    this.isAirborne = heightAboveGround > PHYSICS_TUNING.AIRBORNE_THRESHOLD;

    // Detect ramp exit - launch into air
    if (this.wasOnRamp && !onRamp && heightAboveGround > 0.2) {
      // Just left a ramp - apply launch boost
      const horizontalSpeed = Math.sqrt(
        this.chassis.velocity.x * this.chassis.velocity.x +
        this.chassis.velocity.z * this.chassis.velocity.z
      );
      const launchBoost = horizontalSpeed * PHYSICS_TUNING.LAUNCH_BOOST_FACTOR;

      // Add boost based on ramp height difference (steeper = more boost)
      const rampSteepnessBoost = this.lastGroundHeight * 0.5;

      this.chassis.velocity.y = Math.max(
        this.chassis.velocity.y,
        launchBoost + rampSteepnessBoost
      );

      EventBus.emit('bike:launched', {
        speed: horizontalSpeed,
        launchVelocity: this.chassis.velocity.y,
      });
    }

    // Track airtime
    if (this.isAirborne) {
      this.airTime += dt;

      // Apply reduced gravity for floaty arcade feel
      const gravityScale = PHYSICS_TUNING.AIR_GRAVITY_SCALE;
      this.chassis.velocity.y += PHYSICS_TUNING.BASE_GRAVITY * gravityScale * dt;

    } else {
      // On ground - use spring-damper for smooth height transitions
      if (heightAboveGround < 0) {
        // Below ground - spring force pushes up
        const springForce = -heightAboveGround * PHYSICS_TUNING.SPRING_CONSTANT;
        const dampingForce = -this.chassis.velocity.y * PHYSICS_TUNING.DAMPING;
        this.chassis.velocity.y += (springForce + dampingForce) * dt;

        // Hard floor - don't let bike go below ground
        if (currentY < targetY - 0.1) {
          this.chassis.position.y = targetY - 0.1;
        }
      } else if (heightAboveGround < PHYSICS_TUNING.AIRBORNE_THRESHOLD) {
        // Just above ground - gentle pull down to stick to surface
        const pullForce = -heightAboveGround * PHYSICS_TUNING.SPRING_CONSTANT * 0.5;
        const dampingForce = -this.chassis.velocity.y * PHYSICS_TUNING.DAMPING * 0.5;
        this.chassis.velocity.y += (pullForce + dampingForce) * dt;
      }

      // Detect landing
      if (wasAirborne && !this.isAirborne) {
        this.onLanding();
      }
    }

    // Track state for next frame
    this.wasOnRamp = onRamp;
    this.lastGroundHeight = groundHeight;
  }

  private onLanding(): void {
    const landingVelocity = this.chassis.velocity.y;

    EventBus.emit('bike:landed', {
      airTime: this.airTime,
      velocity: landingVelocity,
      position: {
        x: this.chassis.position.x,
        y: this.chassis.position.y,
        z: this.chassis.position.z,
      },
    });

    // Reset airtime tracker
    this.airTime = 0;
  }

  private stabilize(): void {
    // Extract current yaw
    const q = this.chassis.quaternion;
    const yaw = Math.atan2(
      2 * (q.w * q.y + q.x * q.z),
      1 - 2 * (q.y * q.y + q.z * q.z)
    );

    // Reset to upright with only yaw rotation
    this.chassis.quaternion.setFromAxisAngle(new CANNON.Vec3(0, 1, 0), yaw);

    // Kill any angular velocity on X and Z
    this.chassis.angularVelocity.x = 0;
    this.chassis.angularVelocity.z = 0;
  }

  public getPosition(): CANNON.Vec3 {
    return this.chassis.position;
  }

  public getQuaternion(): CANNON.Quaternion {
    return this.chassis.quaternion;
  }

  public getVelocity(): CANNON.Vec3 {
    return this.chassis.velocity;
  }

  public getSpeed(): number {
    return this.forwardSpeed;
  }

  public getSpeedKmh(): number {
    return Math.abs(this.forwardSpeed) * 3.6;
  }

  public getForwardVector(): CANNON.Vec3 {
    const forward = new CANNON.Vec3(0, 0, 1);
    this.chassis.quaternion.vmult(forward, forward);
    return forward;
  }

  public isInAir(): boolean {
    return this.isAirborne;
  }

  public getAirTime(): number {
    return this.airTime;
  }
}
